import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase';
import { format, startOfMonth, endOfMonth, differenceInCalendarDays, addYears } from 'date-fns';

import Dashboard from './Dashboard'

const DashboardContainer = () => {
    const ANNUAL_TARGET = 25_00_000; // 25 lacs
    const [selectedMonth, setSelectedMonth] = useState('current');
    const [stats, setStats] = useState({
        totalDoctors: 0,
        totalChemists: 0,
        totalContacts: 0,
        visitedDoctors: 0,
        visitPercentage: 0,
        totalSales: 0,
        totalRevenue: 0,
        totalProducts: 0,
        annualTarget: ANNUAL_TARGET,
        daysLeftInFiscalYear: 0,
        targetRemaining: ANNUAL_TARGET
    });
    const [recentVisits, setRecentVisits] = useState([]);
    const [salesData, setSalesData] = useState([]);
    const [monthlySalesData, setMonthlySalesData] = useState([]);
    const [topContacts, setTopContacts] = useState([]);
    const [upcomingDates, setUpcomingDates] = useState([]);

    useEffect(() => {
        fetchDashboardData();
        fetchUpcomingDates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth]);

    const getDateRange = () => {
        if (selectedMonth === 'overall') {
            return { startDate: null, endDate: null };
        } else if (selectedMonth === 'current') {
            const now = new Date();
            return {
                startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
                endDate: format(now, 'yyyy-MM-dd')
            };
        } else {
            const year = parseInt(selectedMonth.split('-')[0]);
            const month = parseInt(selectedMonth.split('-')[1]) - 1;
            const date = new Date(year, month);
            return {
                startDate: format(startOfMonth(date), 'yyyy-MM-dd'),
                endDate: format(endOfMonth(date), 'yyyy-MM-dd')
            };
        }
    };

    const fetchDashboardData = async () => {
        try {
            const { startDate, endDate } = getDateRange();
            const isOverall = selectedMonth === 'overall';

            // Calculate fiscal year (April 1 - March 31)
            const today = new Date();
            let fiscalYearStart = new Date(today.getFullYear(), 3, 1); // April 1
            let fiscalYearEnd = new Date(today.getFullYear() + 1, 2, 31); // March 31 next year
            
            if (today < fiscalYearStart) {
                fiscalYearStart = new Date(today.getFullYear() - 1, 3, 1);
                fiscalYearEnd = new Date(today.getFullYear(), 2, 31);
            }

            const daysLeftInFiscalYear = differenceInCalendarDays(fiscalYearEnd, today) + 1; // +1 to include today

            // Fetch all contacts with their types
            const { data: allContacts } = await supabase
                .from('doctors')
                .select('id, contact_type');

            const totalDoctors = allContacts?.filter(c => c.contact_type === 'doctor').length || 0;
            const totalChemists = allContacts?.filter(c => c.contact_type === 'chemist').length || 0;
            const totalContacts = allContacts?.length || 0;

            const doctorIds = allContacts?.filter(c => c.contact_type === 'doctor').map(c => c.id) || [];

            const { count: totalProductsCount } = await supabase
                .from('products')
                .select('*', { count: 'exact' });

            // Fetch visits for the selected period (for all contacts)
            let visitsQuery = supabase
                .from('visits')
                .select('doctor_id, status');

            if (!isOverall) {
                visitsQuery = visitsQuery
                    .gte('visit_date', startDate)
                    .lte('visit_date', endDate);
            }

            const { data: visits } = await visitsQuery;

            const uniqueDoctorIds = new Set(
                visits?.filter(v => doctorIds.includes(v.doctor_id)).map(v => v.doctor_id) || []
            );
            const visitedDoctorsCount = uniqueDoctorIds.size;

            const visitPercentage = totalDoctors > 0 
                ? ((visitedDoctorsCount / totalDoctors) * 100).toFixed(1)
                : 0;

            // Fetch sales count
            let salesQuery = supabase
                .from('sales')
                .select('*, visits!inner(visit_date)', { count: 'exact' });

            if (!isOverall) {
                salesQuery = salesQuery
                    .gte('visits.visit_date', startDate)
                    .lte('visits.visit_date', endDate);
            }

            const { data: salesData, count: salesCount } = await salesQuery;

            // Calculate total revenue
            const totalRevenue = salesData?.reduce((sum, sale) => sum + parseFloat(sale.total_amount), 0) || 0;

            // Fetch fiscal year revenue (April 1 - today of fiscal year)
            const { data: fiscalYearSalesData } = await supabase
                .from('sales')
                .select('total_amount, visits!inner(visit_date)')
                .gte('visits.visit_date', format(fiscalYearStart, 'yyyy-MM-dd'))
                .lte('visits.visit_date', format(today, 'yyyy-MM-dd'));

            const fiscalYearRevenue = fiscalYearSalesData?.reduce((sum, sale) => sum + parseFloat(sale.total_amount), 0) || 0;
            const targetRemaining = Math.max(0, ANNUAL_TARGET - fiscalYearRevenue);

            setStats({
                totalDoctors,
                totalChemists,
                totalContacts,
                visitedDoctors: visitedDoctorsCount,
                visitPercentage: parseFloat(visitPercentage),
                totalSales: salesCount || 0,
                totalRevenue,
                totalProducts: totalProductsCount || 0,
                annualTarget: ANNUAL_TARGET,
                daysLeftInFiscalYear,
                targetRemaining
            });

            // Fetch recent visits from selected period
            let recentVisitsQuery = supabase
                .from('visits')
                .select(`
                    *,
                    doctors (name, contact_type)
                `)
                .order('visit_date', { ascending: false })
                .limit(5);

            if (!isOverall) {
                recentVisitsQuery = recentVisitsQuery
                    .gte('visit_date', startDate)
                    .lte('visit_date', endDate);
            }

            const { data: recentVisitsData } = await recentVisitsQuery;
            setRecentVisits(recentVisitsData || []);

            // Fetch sales data for charts
            let salesQueryForCharts = supabase
                .from('sales')
                .select(`
                    *,
                    visits!inner (visit_date),
                    products (name)
                `)
                .order('created_at', { ascending: false });

            if (!isOverall) {
                salesQueryForCharts = salesQueryForCharts
                    .gte('visits.visit_date', startDate)
                    .lte('visits.visit_date', endDate);
            }

            const { data: sales } = await salesQueryForCharts;

            // Process sales data for charts - keep sortKey separate from display label
            const salesByDate = {};
            sales?.forEach(sale => {
                const visitDate = new Date(sale.visits.visit_date);
                const sortKey = format(visitDate, 'yyyy-MM-dd');
                const label = format(visitDate, 'MMM dd');
                if (!salesByDate[sortKey]) {
                    salesByDate[sortKey] = { date: label, amount: 0, sortKey };
                }
                salesByDate[sortKey].amount += parseFloat(sale.total_amount);
            });

            const processedSalesData = Object.values(salesByDate)
                .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
                .slice(-10)
                .map(({ date, amount }) => ({ date, amount }));

            setSalesData(processedSalesData);

            // Fetch top contacts by sales
            let topContactsQuery = supabase
                .from('sales')
                .select(`
                    total_amount,
                    visits!inner (
                        visit_date,
                        doctors (name, contact_type)
                    )
                `);

            if (!isOverall) {
                topContactsQuery = topContactsQuery
                    .gte('visits.visit_date', startDate)
                    .lte('visits.visit_date', endDate);
            }

            const { data: topContactsData } = await topContactsQuery;

            const contactSales = {};
            topContactsData?.forEach(sale => {
                const contactName = sale.visits.doctors.name;
                const contactType = sale.visits.doctors.contact_type;
                const key = `${contactName}|${contactType}`;
                
                if (!contactSales[key]) {
                    contactSales[key] = {
                        name: contactName,
                        contact_type: contactType,
                        total: 0
                    };
                }
                contactSales[key].total += parseFloat(sale.total_amount);
            });

            const topContactsProcessed = Object.values(contactSales)
                .sort((a, b) => b.total - a.total)
                .slice(0, 5);

            setTopContacts(topContactsProcessed);

            // Fetch monthly sales data (always last 12 months, independent of filter)
            const twelveMonthsAgo = new Date();
            twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 11);
            twelveMonthsAgo.setDate(1);

            const monthlySalesRaw = [];
            const monthlySalesStartDate = format(twelveMonthsAgo, 'yyyy-MM-dd');
            const monthlySalesEndDate = format(new Date(), 'yyyy-MM-dd');
            const pageSize = 1000;

            for (let offset = 0; ; offset += pageSize) {
                const { data: salesPage, error: monthlySalesError } = await supabase
                    .from('sales')
                    .select('total_amount, visits!inner(visit_date)')
                    .gte('visits.visit_date', monthlySalesStartDate)
                    .lte('visits.visit_date', monthlySalesEndDate)
                    .range(offset, offset + pageSize - 1);

                if (monthlySalesError) throw monthlySalesError;

                monthlySalesRaw.push(...(salesPage || []));
                if (!salesPage || salesPage.length < pageSize) break;
            }

            const monthlyBuckets = {};
            monthlySalesRaw?.forEach(sale => {
                const key = format(new Date(sale.visits.visit_date), 'MMM yyyy');
                monthlyBuckets[key] = (monthlyBuckets[key] || 0) + parseFloat(sale.total_amount);
            });

            const months = [];
            for (let i = 11; i >= 0; i--) {
                const d = new Date();
                d.setDate(1);
                d.setMonth(d.getMonth() - i);
                const key = format(d, 'MMM yyyy');
                months.push({ month: key, sales: monthlyBuckets[key] || 0 });
            }
            setMonthlySalesData(months);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

    const fetchUpcomingDates = async () => {
        try {
            const { data, error } = await supabase
                .from('doctor_important_dates')
                .select('*, doctors(name, contact_type)');

            if (error) throw error;

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const withUpcoming = (data || []).map(entry => {
                const dateObj = new Date(entry.date + 'T00:00:00');
                let nextOccurrence;
                if (entry.is_recurring) {
                    // Recurring: find next annual occurrence
                    nextOccurrence = new Date(today.getFullYear(), dateObj.getMonth(), dateObj.getDate());
                    if (nextOccurrence < today) {
                        nextOccurrence = addYears(nextOccurrence, 1);
                    }
                } else {
                    // One-time: use the exact date
                    nextOccurrence = dateObj;
                }
                const daysUntil = differenceInCalendarDays(nextOccurrence, today);
                return { ...entry, daysUntil, nextOccurrence };
            });

            const upcoming = withUpcoming
                .filter(entry => entry.daysUntil >= 0 && entry.daysUntil <= 7)
                .sort((a, b) => a.daysUntil - b.daysUntil);

            setUpcomingDates(upcoming);
        } catch (error) {
            console.error('Error fetching upcoming dates:', error);
        }
    };

    const generateMonthOptions = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        
        const options = [
            { value: 'current', label: 'This Month', group: 'quick' },
            { value: 'overall', label: 'All Time', group: 'quick' }
        ];

        if (currentMonth > 0) {
            for (let month = currentMonth - 1; month >= 0; month--) {
                const date = new Date(currentYear, month, 1);
                const value = format(date, 'yyyy-MM');
                const label = format(date, 'MMMM yyyy');
                options.push({ value, label, group: 'thisYear' });
            }
        }

        return options;
    };

    return (
        <Dashboard
            stats={stats}
            recentVisits={recentVisits}
            salesData={salesData}
            monthlySalesData={monthlySalesData}
            topContacts={topContacts}
            upcomingDates={upcomingDates}
            COLORS={COLORS}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            monthOptions={generateMonthOptions()}
        />
    )
}

export default DashboardContainer